import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  X, MapPin, Thermometer, ShieldAlert, Loader2, ExternalLink,
  CheckCircle2, Wind, Flame, Snowflake, ArrowRight,
} from "lucide-react";
import { lookupLocalCodeForDesign } from "../lib/api";

/**
 * V2.0.11 — In-Editor "// LOCAL CODE" dialog.
 *
 * Wraps the public Local Code Finder result for the LOGGED-IN user's
 * current design. Pulls the design's `build_site_metadata.zip_code` +
 * state from the backend, fetches the same lookup as the public page,
 * and shows the IECC zone + state code + mandates + hazards + ranges
 * inline. Includes a "Run this against my current design" button that
 * triggers the existing CODE CHECK flow (handled by the parent).
 */
export default function LocalCodeDialog({
  open,
  designId,
  onClose,
  onRunCodeCheck,
  // V2.0.35 — Inline ZIP entry support. When the design has no ZIP
  // yet (the common landing state), the dialog renders a one-input
  // form right here instead of redirecting the user to View Options.
  currentZip = "",
  currentState = "",
  currentCity = "",
  onSaveLocation,
}) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [data, setData] = useState(null);
  const [zipInput, setZipInput] = useState(currentZip);
  const [stateInput, setStateInput] = useState(currentState);
  const [cityInput, setCityInput] = useState(currentCity);
  const [savingZip, setSavingZip] = useState(false);
  const [fetchTick, setFetchTick] = useState(0);

  // Re-sync local form state when the dialog opens or the design's
  // build-site metadata changes underneath us.
  useEffect(() => {
    if (open) {
      setZipInput(currentZip || "");
      setStateInput(currentState || "");
      setCityInput(currentCity || "");
    }
  }, [open, currentZip, currentState, currentCity]);

  useEffect(() => {
    if (!open || !designId) return;
    let cancelled = false;
    setLoading(true);
    setErr("");
    setData(null);
    lookupLocalCodeForDesign(designId)
      .then((res) => {
        if (cancelled) return;
        setData(res.data?.summary || null);
      })
      .catch((e) => {
        if (cancelled) return;
        setErr(e?.response?.data?.detail || "Could not load local code data.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, designId, fetchTick]);

  // ZIP missing? show the inline entry form. Backend returns the
  // detail "Design has no ZIP code yet …" — also surface our own form
  // when the saved ZIP is just empty.
  const needsZip = !loading && (
    !currentZip ||
    (err && /zip/i.test(err))
  );

  const canSaveZip = (zipInput || "").trim().length === 5 && typeof onSaveLocation === "function";

  const handleSaveZip = async () => {
    if (!canSaveZip) return;
    setSavingZip(true);
    try {
      const parts = [cityInput.trim(), stateInput.trim(), zipInput.trim()].filter(Boolean);
      const label = parts.length ? parts.join(", ").replace(/,\s*(\d{5})$/, " $1") : null;
      await onSaveLocation({
        city: cityInput.trim() || undefined,
        state: stateInput.trim() || undefined,
        zip_code: zipInput.trim(),
        country: "US",
        label,
      });
      setErr("");
      // Trigger a refetch — the backend now has the ZIP and can
      // return real local-code data.
      setFetchTick((n) => n + 1);
    } finally {
      setSavingZip(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="local-code-dialog"
      className="fixed inset-0 z-50 bg-zinc-950/80 backdrop-blur-sm flex items-start justify-center p-4 sm:p-8 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl bg-zinc-900 border border-brass-500/40 shadow-[0_24px_64px_rgba(0,0,0,0.6)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-brass-400" />
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-brass-300">
              // Local Code · Site-Specific Rules
            </div>
          </div>
          <button
            type="button"
            data-testid="local-code-dialog-close"
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading && (
          <div className="px-5 py-10 flex items-center gap-3 text-zinc-400 font-mono text-xs uppercase tracking-widest">
            <Loader2 className="w-4 h-4 animate-spin" />
            Looking up your site...
          </div>
        )}

        {/* V2.0.35 — Inline ZIP entry. Replaces the previous "open
            View Options → Build Site" redirect text, which sent users
            on a hunt through a panel section labelled "Build Location"
            instead. Now they can enter their ZIP right here and the
            dialog auto-refetches with the real local code data. */}
        {needsZip && !loading && (
          <div className="px-5 py-5 space-y-4">
            <div
              data-testid="local-code-zip-entry"
              className="border border-brass-700/60 bg-brass-950/30 p-4"
            >
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-brass-300 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-brass-200 mb-1">
                    // Site address required
                  </div>
                  <p className="text-sm text-zinc-300 leading-relaxed mb-3">
                    Add the build-site ZIP so we can fetch the IECC zone, state
                    code, FEMA hazards, and energy mandates for this design.
                  </p>
                  <div className="grid grid-cols-12 gap-1.5">
                    <input
                      value={cityInput}
                      onChange={(e) => setCityInput(e.target.value)}
                      placeholder="City"
                      data-testid="local-code-city-input"
                      className="col-span-7 bg-zinc-900 border border-zinc-800 focus:border-brass-500 outline-none px-2.5 py-2 font-mono text-xs text-zinc-100"
                    />
                    <input
                      value={stateInput}
                      onChange={(e) => setStateInput(e.target.value.toUpperCase().slice(0, 2))}
                      placeholder="ST"
                      maxLength={2}
                      data-testid="local-code-state-input"
                      className="col-span-2 bg-zinc-900 border border-zinc-800 focus:border-brass-500 outline-none px-2 py-2 font-mono text-xs text-zinc-100 text-center"
                    />
                    <input
                      value={zipInput}
                      onChange={(e) => setZipInput(e.target.value.replace(/\D/g, "").slice(0, 5))}
                      placeholder="ZIP"
                      inputMode="numeric"
                      data-testid="local-code-zip-input"
                      className="col-span-3 bg-zinc-900 border border-zinc-800 focus:border-brass-500 outline-none px-2 py-2 font-mono text-xs text-zinc-100"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleSaveZip}
                    disabled={!canSaveZip || savingZip}
                    data-testid="local-code-save-zip-btn"
                    className={
                      "mt-3 w-full inline-flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-widest px-3 py-2 border transition-colors " +
                      (canSaveZip && !savingZip
                        ? "border-brass-500 bg-brass-500 text-zinc-950 hover:bg-brass-400"
                        : "border-zinc-800 text-zinc-500 cursor-not-allowed")
                    }
                  >
                    {savingZip ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <ArrowRight className="w-3.5 h-3.5" />
                    )}
                    {savingZip ? "Looking up your site…" : "Save & Lookup"}
                  </button>
                </div>
              </div>
            </div>
            <Link
              to="/local-code-finder"
              data-testid="local-code-dialog-public-link"
              className="block text-center font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-brass-300 transition-colors"
            >
              Or try the public Local Code Finder →
            </Link>
          </div>
        )}

        {err && !needsZip && (
          <div className="px-5 py-6">
            <div
              data-testid="local-code-dialog-error"
              className="border border-amber-500/40 bg-amber-500/10 p-4 text-amber-200 text-sm leading-relaxed"
            >
              {err}
            </div>
          </div>
        )}

        {data && !loading && !err && (
          <DialogBody data={data} designId={designId} onRunCodeCheck={onRunCodeCheck} />
        )}
      </div>
    </div>
  );
}


function DialogBody({ data, designId, onRunCodeCheck }) {
  const code = data.state_code || {};
  const ins = data.iecc_insulation;
  const mandates = data.energy_mandates || [];
  const hazards = data.hazards || {};
  const ranges = data.typical_ranges || {};

  const hazardChips = useMemo(() => {
    const out = [];
    if (hazards.wildland_urban_interface)
      out.push({ icon: Flame, label: "Wildfire (WUI) likely", color: "amber" });
    if (hazards.hurricane_zone)
      out.push({ icon: Wind, label: "Hurricane / coastal wind zone", color: "rose" });
    if (hazards.heavy_snow_load)
      out.push({ icon: Snowflake, label: "Heavy snow load region", color: "sky" });
    return out;
  }, [hazards]);

  return (
    <div className="px-5 py-5 space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            // SITE
          </div>
          <div
            data-testid="local-code-dialog-zip"
            className="text-zinc-50 text-xl font-display tracking-tight"
          >
            ZIP {data.zip_code} · {data.state || "—"}
          </div>
        </div>
        <a
          href={data.fema_flood_lookup}
          target="_blank"
          rel="noreferrer"
          data-testid="local-code-dialog-fema-link"
          className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest border border-zinc-700 px-3 py-2 text-zinc-300 hover:border-brass-500 hover:text-brass-300 transition-colors"
        >
          FEMA flood map <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <Card icon={Thermometer} label="IECC zone" testid="local-code-card-iecc">
          <div className="text-brass-300 font-display text-3xl leading-none">
            {data.iecc_climate_zone || "—"}
          </div>
          {ins && (
            <div className="mt-3 text-xs text-zinc-300 space-y-0.5 font-mono">
              <div>Ceiling R-{ins.roof}</div>
              <div>Wall R-{ins.wall}</div>
              <div>Window U {ins.window_u}</div>
            </div>
          )}
        </Card>
        <Card icon={MapPin} label="State code" testid="local-code-card-state-code">
          <div className="text-zinc-100 text-sm font-medium leading-tight">
            {code.title || "—"}
          </div>
          <div className="mt-1 text-xs text-zinc-400 leading-relaxed">{code.base}</div>
          {code.is_unique && (
            <div className="mt-2 inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest border border-amber-500/40 bg-amber-500/10 text-amber-300 px-2 py-0.5">
              State-unique
            </div>
          )}
        </Card>
        <Card icon={ShieldAlert} label="Hazards" testid="local-code-card-hazards">
          {hazardChips.length === 0 ? (
            <div className="text-zinc-400 text-xs leading-relaxed">
              No major overlays.
            </div>
          ) : (
            <div className="space-y-1.5">
              {hazardChips.map(({ icon: Icon, label, color }) => (
                <div key={label} className={`flex items-start gap-1.5 text-xs text-${color}-200`}>
                  <Icon className={`w-3 h-3 mt-0.5 flex-shrink-0 text-${color}-400`} />
                  <span>{label}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {mandates.length > 0 && (
        <div
          data-testid="local-code-dialog-mandates"
          className="border border-brass-500/30 bg-brass-500/5 p-4"
        >
          <div className="font-mono text-[10px] uppercase tracking-widest text-brass-300">
            // State energy mandates
          </div>
          <ul className="mt-2 space-y-1.5">
            {mandates.map((m) => (
              <li key={m} className="flex items-start gap-2 text-zinc-200 text-xs leading-relaxed">
                <CheckCircle2 className="w-3 h-3 mt-0.5 flex-shrink-0 text-brass-400" />
                <span>{m}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
          // Typical residential ranges
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {Object.entries(ranges).map(([key, r]) => (
            <div
              key={key}
              data-testid={`local-code-dialog-range-${key}`}
              className="border border-zinc-800 p-3 hover:border-brass-500/40 transition-colors"
            >
              <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-400">
                {r.label}
              </div>
              <div className="mt-1 flex items-baseline gap-1">
                <span className="text-brass-300 font-display text-lg">{r.typical}</span>
                <span className="font-mono text-[9px] uppercase text-zinc-500">typ</span>
              </div>
              <div className="text-[10px] text-zinc-500 font-mono">{r.low}–{r.high}</div>
            </div>
          ))}
        </div>
      </div>

      <div
        data-testid="local-code-dialog-advisory"
        className="border border-amber-500/30 bg-amber-500/5 p-3 text-amber-200/90 text-xs leading-relaxed"
      >
        <b>Advisory only.</b> Verify with your local AHJ before construction.
        Last reviewed: {data.last_reviewed_at}.
      </div>

      <div className="flex flex-col sm:flex-row items-stretch gap-2 pt-1">
        <button
          type="button"
          data-testid="local-code-dialog-run-codecheck-btn"
          onClick={() => onRunCodeCheck?.()}
          className="flex-1 inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-mono text-[11px] uppercase tracking-widest px-4 py-3 transition-colors"
        >
          <ShieldAlert className="w-4 h-4" />
          Run this against my design
        </button>
        <Link
          to={`/local-code-finder?zip=${data.zip_code}${data.state ? `&state=${data.state}` : ""}`}
          target="_blank"
          rel="noreferrer"
          data-testid="local-code-dialog-share-link"
          className="inline-flex items-center justify-center gap-2 border border-zinc-700 hover:border-brass-500 text-zinc-200 hover:text-brass-200 font-mono text-[11px] uppercase tracking-widest px-4 py-3 transition-colors"
        >
          Open full report <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
}


function Card({ icon: Icon, label, children, testid }) {
  return (
    <div
      data-testid={testid}
      className="border border-zinc-800 bg-zinc-950 p-4 hover:border-brass-500/40 transition-colors"
    >
      <div className="flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5 text-brass-400" />
        <div className="font-mono text-[9px] uppercase tracking-widest text-brass-300">
          // {label}
        </div>
      </div>
      <div className="mt-2">{children}</div>
    </div>
  );
}
